import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CheckboxField,
  Flex,
  Heading,
  SelectField,
  Text,
  View,
} from '@aws-amplify/ui-react';
import { Link } from 'react-router-dom';
import Loadingmini from '../components/loadingmini';
import GigTenantSummary from '../components/GigTenantSummary';
import { api3Get, api3Post } from '../api/api3Client';

const STATE_FILTERS = [
  { value: 'pending', label: 'Awaiting review' },
  { value: 'approved', label: 'Approved, not yet executed' },
  { value: 'executed', label: 'Executed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'failed', label: 'Failed' },
  { value: 'superseded', label: 'Withdrawn by a later run' },
];

const ACTION_FILTERS = [
  { value: 'disconnect', label: 'Disconnections' },
  { value: 'repair', label: 'Repair (bookkeeping only)' },
  { value: 'all', label: 'Everything' },
];

const badge = (background, color) => ({
  background,
  color,
  padding: '2px 6px',
  borderRadius: '4px',
  whiteSpace: 'nowrap',
});

function voteStyle(vote) {
  if (vote === 'cull') return badge('#fde8e8', '#9b1c1c');
  if (vote === 'keep') return badge('#e6f4ea', '#1e4620');
  return badge('#eee', '#444');
}

function passStyle(pass) {
  if (pass === 'reconciliation') return badge('#fff4e5', '#7a4a00');
  return badge('#e8f4fd', '#1c4a6b');
}

function formatTimestamp(ts) {
  if (ts === undefined || ts === null || ts === '') return '—';
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return String(ts);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function IdCell({ value }) {
  if (!value) return '—';
  return (
    <Text
      fontSize="0.75rem"
      style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
      title={value}
    >
      {value}
    </Text>
  );
}

function FleetRules({ rules }) {
  const [open, setOpen] = useState(false);
  if (!rules?.length) return null;
  return (
    <View marginTop="1rem">
      <Button size="small" onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Show'} the cull rule in effect for each fleet
      </Button>
      {open ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
          <thead>
            <tr>
              <th align="left">Fleet</th>
              <th align="left">Culling</th>
              <th align="left">Window</th>
              <th align="left">Rule</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.fleetId}>
                <td>
                  {r.fleetCode} ({r.name})
                </td>
                <td>
                  <span style={r.cullEnabled ? voteStyle('cull') : voteStyle('keep')}>
                    {r.cullEnabled ? 'enabled' : 'disabled'}
                  </span>
                </td>
                <td>
                  {r.windowDays} days
                  {r.flooredForSafety ? (
                    <Text as="span" fontSize="0.8rem">
                      {' '}
                      (raised from {r.configuredStaleDays} by the 30-day floor)
                    </Text>
                  ) : null}
                </td>
                <td>{r.ruleText}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </View>
  );
}

function EffectsCell({ row }) {
  if (row.action === 'repair') {
    return (
      <Text fontSize="0.8rem" color="#666">
        bookkeeping only
      </Text>
    );
  }
  const effects = row.plannedEffects || {};
  return (
    <View>
      <Text fontSize="0.8rem" display="block">
        {row.argyleDeleteLabel || effects.argyleDeleteLabel || '—'}
      </Text>
      <Text fontSize="0.8rem" display="block" color="#444">
        {row.platformAccountsLabel || effects.platformAccountsLabel || '—'}
      </Text>
    </View>
  );
}

function ProposalDetail({ row }) {
  const evidence = row.evidence || {};
  return (
    <View padding="0.5rem 0.5rem 0.75rem 2rem" backgroundColor="#fafafa">
      <Text fontSize="0.9rem">
        Gig provider user {row.gigProviderUserId || row.jobUserId || '—'} · gig account{' '}
        {row.gigAccountId || row.jobAccountId || '—'} · subject {row.subjectType}{' '}
        {row.subjectId} on {row.jobApi} tenant {row.jobApiInfoId}
      </Text>
      <Text fontSize="0.9rem">
        Last reservation: {formatTimestamp(row.lastReservationAt)}
      </Text>
      <Text fontSize="0.9rem" marginTop="0.25rem">
        Planned effects (if approved and still valid at execute):{' '}
        {row.effectsSummary || row.plannedEffects?.summary || '—'}
      </Text>
      <Text fontSize="0.8rem" color="#666">
        Argyle delete removes the provider user or gig account at the provider. On our
        side, <code>platform_accounts</code> rows with trip history are stamped removed
        (<code>provider_acct_removed_at</code>); rows with no history are purged.
      </Text>

      <Heading level={6} marginTop="0.5rem">
        Fleet votes
      </Heading>
      {row.votes?.length ? (
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {row.votes.map((v, i) => (
              <tr key={`${v.fleet_code || v.fleet_id}-${i}`}>
                <td style={{ paddingRight: '0.75rem' }}>{v.fleet_code || v.fleet_id}</td>
                <td style={{ paddingRight: '0.75rem' }}>
                  <span style={voteStyle(v.vote)}>{v.vote}</span>
                </td>
                <td style={{ paddingRight: '0.75rem' }}>
                  {v.last_drove ? `last drove ${formatTimestamp(v.last_drove)}` : 'no reservation on record'}
                </td>
                <td>{v.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Text fontSize="0.9rem">
          No fleet voted — this person holds no active membership anywhere.
        </Text>
      )}

      <Heading level={6} marginTop="0.5rem">
        Evidence
      </Heading>
      <pre style={{ fontSize: '0.8rem', margin: 0, whiteSpace: 'pre-wrap' }}>
        {JSON.stringify(evidence, null, 2)}
      </pre>
    </View>
  );
}

export default function AdminGigDisconnect() {
  const [stateFilter, setStateFilter] = useState('pending');
  const [actionFilter, setActionFilter] = useState('disconnect');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [rows, setRows] = useState([]);
  const [rules, setRules] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [pendingDisconnects, setPendingDisconnects] = useState(0);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  async function loadList(overrides) {
    const s = overrides?.state ?? stateFilter;
    const a = overrides?.action ?? actionFilter;
    setLoading(true);
    setError('');
    try {
      const res = await api3Get('/admin/portal/gig-disconnect', {
        state: s,
        actionFilter: a,
        limit: 200,
      });
      setRows(res.proposals || []);
      setRules(res.fleetRules || []);
      setTenants(res.tenantSummaries || []);
      setPendingDisconnects(res.pendingDisconnects || 0);
      setSelected([]);
    } catch (e) {
      setError(e.message || String(e));
      setRows([]);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter, actionFilter]);

  const approvable = rows.filter((r) => r.state === 'pending' && r.action === 'disconnect');
  const selectable = rows.filter((r) => r.state === 'pending');

  function toggle(id) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleAllVisible() {
    const ids = selectable.map((r) => r.id);
    setSelected((prev) => (prev.length === ids.length ? [] : ids));
  }

  function requestSelected(action) {
    if (!selected.length) return;
    const verb = action === 'approve' ? 'Approve' : 'Reject';
    setConfirm({
      kind: 'ids',
      action,
      ids: selected,
      label: `${verb} ${selected.length} selected proposal${selected.length === 1 ? '' : 's'}?`,
      detail:
        action === 'approve'
          ? 'Each one is re-checked against live Argyle state before anything is disconnected. Drivers who resumed in the meantime will be skipped.'
          : 'Rejected proposals are dismissed. A later run may propose them again if the driver still looks stale.',
    });
  }

  function requestApproveAll() {
    setConfirm({
      kind: 'all',
      action: 'approve',
      expectedCount: pendingDisconnects,
      label: `Approve all ${pendingDisconnects} pending disconnect${pendingDisconnects === 1 ? '' : 's'}?`,
      detail:
        'This approves every pending disconnection, including any not shown on this page. Each is still re-checked against live Argyle state before it is acted on.',
    });
  }

  async function executeConfirm() {
    if (!confirm) return;
    setActionLoading(true);
    setError('');
    setInfo('');
    try {
      const body =
        confirm.kind === 'all'
          ? { action: 'approve', all: true, expectedCount: confirm.expectedCount }
          : { action: confirm.action, ids: confirm.ids };
      const res = await api3Post('/admin/portal/gig-disconnect/action', body);
      setConfirm(null);
      if (confirm.kind === 'all') {
        setInfo(`Approved ${res.approved} proposal${res.approved === 1 ? '' : 's'}.`);
      } else {
        const skipped = res.unchanged?.length
          ? ` ${res.unchanged.length} were already decided and left alone.`
          : '';
        setInfo(`${res.changed} proposal${res.changed === 1 ? '' : 's'} moved to ${res.newState}.${skipped}`);
      }
      await loadList();
    } catch (e) {
      // 409 means the queue moved between page load and click. Reload rather
      // than retrying with the new number: the point of the count is that a
      // human looked at these specific rows.
      if (e.status === 409) {
        setConfirm(null);
        setError(e.message || 'The queue changed while you were reviewing it.');
        await loadList();
      } else {
        setError(e.message || String(e));
      }
    }
    setActionLoading(false);
  }

  return (
    <View padding="1rem">
      <Heading level={3}>Admin: Gig Disconnect Queue</Heading>
      <Text>
        Proposed Argyle disconnections written by perioddata. Nothing here has been
        disconnected yet. Approving marks a proposal for the executor, which re-checks it
        against live Argyle state before acting — a driver who started driving again is
        dropped rather than cut off. Driving history is never deleted: an account with
        activity keeps its rows and is simply marked disconnected. To browse every
        connection and its live keep/cull votes, see{' '}
        <Link to="/admin/gig-accounts">Gig Account Status</Link>.
      </Text>
      <Text fontSize="0.9rem" marginTop="0.35rem" color="#444">
        <strong>Last reservation</strong> is the latest{' '}
        <code>insurance_schedule_table.end</code> for that person across all fleets (real
        booked coverage), not <code>res_ledger</code>. Blank when there is no person on the
        proposal or no schedule rows.
      </Text>

      <FleetRules rules={rules} />
      <GigTenantSummary tenants={tenants} />

      <Flex direction="row" gap="0.5rem" alignItems="flex-end" wrap="wrap" marginTop="1rem">
        <SelectField
          label="State"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
        >
          {STATE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Kind"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
        >
          {ACTION_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </SelectField>
        <Button onClick={() => loadList()} isLoading={loading}>
          Refresh
        </Button>
      </Flex>

      <Text marginTop="0.75rem">
        {pendingDisconnects} pending disconnect{pendingDisconnects === 1 ? '' : 's'} in the
        queue. Showing {rows.length}.
      </Text>

      {error ? (
        <Alert variation="error" isDismissible={false} marginTop="1rem">
          {error}
        </Alert>
      ) : null}

      {info ? (
        <Alert variation="success" isDismissible marginTop="1rem" onDismiss={() => setInfo('')}>
          {info}
        </Alert>
      ) : null}

      <Flex direction="row" gap="0.5rem" wrap="wrap" marginTop="1rem">
        <Button
          onClick={() => requestSelected('approve')}
          isDisabled={!selected.length || actionLoading}
        >
          Approve selected ({selected.length})
        </Button>
        <Button
          onClick={() => requestSelected('reject')}
          isDisabled={!selected.length || actionLoading}
        >
          Reject selected ({selected.length})
        </Button>
        <Button
          variation="primary"
          onClick={requestApproveAll}
          isDisabled={!pendingDisconnects || actionLoading}
        >
          Approve all {pendingDisconnects} pending
        </Button>
      </Flex>

      {confirm ? (
        <Alert variation="warning" isDismissible={false} marginTop="1rem">
          <Text fontWeight="bold">{confirm.label}</Text>
          <Text fontSize="0.9rem">{confirm.detail}</Text>
          <Flex direction="row" gap="0.5rem" marginTop="0.5rem">
            <Button variation="primary" onClick={executeConfirm} isLoading={actionLoading}>
              Yes, {confirm.action}
            </Button>
            <Button onClick={() => setConfirm(null)} isDisabled={actionLoading}>
              Cancel
            </Button>
          </Flex>
        </Alert>
      ) : null}

      {loading ? <Loadingmini /> : null}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
        <thead>
          <tr>
            <th align="left">
              <CheckboxField
                label=""
                labelHidden
                name="selectAll"
                checked={selectable.length > 0 && selected.length === selectable.length}
                onChange={toggleAllVisible}
              />
            </th>
            <th align="left">Driver</th>
            <th align="left">Gig provider user</th>
            <th align="left">Gig account</th>
            <th align="left">Last reservation</th>
            <th align="left">If approved</th>
            <th align="left">Subject</th>
            <th align="left">Pass</th>
            <th align="left">Why</th>
            <th align="left">Proposed</th>
            <th align="left">State</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr
                style={{
                  cursor: 'pointer',
                  background: expanded === row.id ? '#eef6ff' : undefined,
                }}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <CheckboxField
                    label=""
                    labelHidden
                    name={`sel-${row.id}`}
                    checked={selected.includes(row.id)}
                    onChange={() => toggle(row.id)}
                    isDisabled={row.state !== 'pending'}
                  />
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  {row.driverName || '—'}
                  {row.userId ? ` (${row.userId})` : ''}
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  <IdCell value={row.gigProviderUserId || row.jobUserId} />
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  <IdCell value={row.gigAccountId || row.jobAccountId} />
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  {formatTimestamp(row.lastReservationAt)}
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  <EffectsCell row={row} />
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  {row.subjectType === 'job_user' ? 'Provider user' : 'One account'}
                  {row.action === 'repair' ? (
                    <Text as="span" fontSize="0.8rem">
                      {' '}
                      — repair only
                    </Text>
                  ) : null}
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  <span style={passStyle(row.pass)}>{row.pass}</span>
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  {row.reason}
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  {formatTimestamp(row.createdAt)}
                </td>
                <td onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                  {row.state}
                  {row.error ? (
                    <Text as="span" fontSize="0.8rem">
                      {' '}
                      — {row.error}
                    </Text>
                  ) : null}
                </td>
              </tr>
              {expanded === row.id ? (
                <tr>
                  <td colSpan={11}>
                    <ProposalDetail row={row} />
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
          {!loading && !rows.length ? (
            <tr>
              <td colSpan={11}>
                <Text padding="1rem">Nothing in this view.</Text>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {actionFilter !== 'disconnect' && approvable.length !== rows.length ? (
        <Text marginTop="0.75rem" fontSize="0.9rem">
          Repair rows record a person with an active membership but no user_to_job link.
          They never disconnect anyone and cannot be approved — dismiss them with Reject
          once the underlying link is fixed.
        </Text>
      ) : null}
    </View>
  );
}
